import clsx from "clsx";
import React, { FC, useEffect, useState } from "react";
import { usePrevious, useThrottle, useWindowScroll } from "react-use";

import { useHeaderMenu } from "../../../hooks/use-header-menu";
import { HeaderMenuSpecialItems } from "../types";
import { HeaderMenuActions } from "./header-menu-actions";
import { HeaderMenuItemDetail } from "./header-menu-item-detail";
import { HeaderMenuItems } from "./header-menu-items";
import { HeaderMenuMain } from "./header-menu-main";
import {
  LocaleSelectorSpecialItem,
  PersonalAreaSpecialItem,
  SearchSpecialItem,
} from "./header-menu-special-items";

export interface HeaderMenuMobileProps {
  className?: string;
  container?: boolean;
}

export const HeaderMenuMobile: FC<HeaderMenuMobileProps> = ({ className }) => {
  const {
    isMenuOpen,
    isSpecialItemOpen,
    expandedItemId,
    headerMenu: { primaryItems, secondaryItems },
  } = useHeaderMenu();

  const threshold = 10;
  const { y } = useWindowScroll();
  const previousScroll = usePrevious(y);
  const [shouldShowMenu, setShouldShowMenu] = useState(true);
  useEffect(() => {
    const deltaY = y - (previousScroll ?? 0);
    if (y < window.innerHeight || isMenuOpen || deltaY < -threshold) {
      setShouldShowMenu(true);
    } else if (deltaY > threshold) {
      setShouldShowMenu(false);
    }
  }, [y, previousScroll, isMenuOpen]);

  const shouldShowMenuThrottled = useThrottle(shouldShowMenu, 200);

  const bgClassName = isMenuOpen && !isSpecialItemOpen ? "bg-primary text-light" : "bg-neutral";
  return (
    <div
      className={clsx(
        "sticky z-[100] top-0 lg:hidden",
        "flex flex-col gap-6 py-4 transition-[background-color,transform] duration-300",
        shouldShowMenuThrottled
          ? "border border-b-neutral-dark border-opacity-50"
          : "-translate-y-[100%]",
        isMenuOpen && "h-screen",
        bgClassName,
        className
      )}
    >
      <HeaderMenuMain className="px-6" />

      {isMenuOpen &&
        (expandedItemId ? (
          <div className="px-5 light-scrollbar overflow-y-auto">
            {(() => {
              switch (expandedItemId) {
                case HeaderMenuSpecialItems.LocaleSelector:
                  return <LocaleSelectorSpecialItem />;
                case HeaderMenuSpecialItems.Search:
                  return <SearchSpecialItem />;
                case HeaderMenuSpecialItems.PersonalArea:
                  return <PersonalAreaSpecialItem />;
                default:
                  return <HeaderMenuItemDetail />;
              }
            })()}
          </div>
        ) : (
          <>
            <HeaderMenuActions className="pl-6 pr-2" />
            {(!!primaryItems?.length || !!secondaryItems?.length) && (
              <HeaderMenuItems className="pl-6 pr-2 pb-12 light-scrollbar overflow-y-auto" />
            )}
          </>
        ))}
    </div>
  );
};
